import { differenceInCalendarDays, isSameDay, parseISO, startOfDay } from 'date-fns';

export interface CooldownData {
  lastCompletionDate?: string;
}

export interface CooldownResult extends CooldownData {
  daysRemaining?: number;
}

interface CooldownEligibilityResult {
  eligible: boolean;
  reason?: 'same-day' | 'cooldown';
  cooldownData?: CooldownResult;
}

const COOLDOWN_DAYS = 10;
const PREVENT_SAME_DAY = true;

/**
 * Checks if the user is eligible for a brain break based on cooldown data.
 * @param data - The cooldown data from React state (hydrated from DB), or null/undefined if no completion recorded
 * @returns CooldownEligibilityResult indicating if user is eligible and any cooldown info
 */
export const checkCooldownEligibility = (data: CooldownData | undefined | null): CooldownEligibilityResult => {
  try {
    if (!data || !data.lastCompletionDate) {
      return { eligible: true };
    }

    const lastCompletion = startOfDay(parseISO(data.lastCompletionDate));
    if (isNaN(lastCompletion.getTime())) {
      return { eligible: true };
    }

    const today = startOfDay(new Date());

    // Check same-day prevention
    if (PREVENT_SAME_DAY && isSameDay(lastCompletion, today)) {
      return { eligible: false, reason: 'same-day', cooldownData: data };
    }

    // Check cooldown period (10 full days; eligible on day 10)
    const daysSinceLastCompletion = differenceInCalendarDays(today, lastCompletion);
    if (daysSinceLastCompletion < COOLDOWN_DAYS) {
      return {
        eligible: false,
        reason: 'cooldown',
        cooldownData: { ...data, daysRemaining: COOLDOWN_DAYS - daysSinceLastCompletion },
      };
    }

    return { eligible: true, cooldownData: data };
  } catch {
    return { eligible: true };
  }
};
